import React, { useCallback, useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import "@/common/sensor";
import apis from "@/common/net/apis";
import {
  APP_OPEN,
  BOTTOM_CTA_TEXT,
  DANMAKU_VISIBLE_COUNT,
  MOCK_DANMAKU_POOL,
  OPEN_BTN_IMG_URL,
  PAGE_TITLE,
  SHARE_GUIDE_TEXT,
  SHARE_H5_BG_IMG,
  SHARE_H5_BTN_IMG,
} from "./constants";
import type { DanmakuItem } from "./constants";
import "@/assets/styles/liveSharePage.less";

import {
  formatLiveShareCurrentDate,
  goTrick,
  LIVE_SHARE_DATE_LABEL_LAYOUT,
} from "./utils";
import { downloadAtOnce } from "@/apps/githubRelate/pages/util";
import { getEnv, tryOpenAppOrDownload } from "@/utils/common";
import { Toast } from "antd-mobile";

/**
 * 与 mcdFun/nfc 下载页一致：jsApiList 为空时部分安卓机型开放标签异常。
 * githubRelate 虽写 []，但直播间页与多页面对齐非空更稳。
 */
const WX_JS_API_LIST = ["updateAppMessageShareData", "updateTimelineShareData"] as const;

/** 与 githubRelate 微信开放标签一致 */
const WX_OPEN_LAUNCH_STYLE: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: 12,
};

const wxOpenLaunchListenerMap = new WeakMap<
  HTMLElement,
  { onLaunch: (e: Event) => void; onError: (e: Event) => void }
>();

const LiveSharePage: React.FC = () => {
  const pool = MOCK_DANMAKU_POOL;
  const poolLen = pool.length;

  /** 须与 wx.config 的 appId 完全一致（github 页写死 wx6d8030a2f43b09a2 与 data.appId 一致）；勿用与签名不一致的 getWxOpenAppAppId */
  // const [wxOaAppId, setWxOaAppId] = useState("");
  const bindWxOpenDelayIdsRef = useRef<number[]>([]);

  const launchExtInfo = APP_OPEN.liveExtInfo;

  const bindWxOpenLaunchListeners = useCallback(() => {
    (["launch-share-top", "launch-share-footer"] as const).forEach((id) => {
      const el = document.getElementById(id) as HTMLElement | null;
      if (!el) return;

      const prev = wxOpenLaunchListenerMap.get(el);
      if (prev) {
        el.removeEventListener("launch", prev.onLaunch);
        el.removeEventListener("error", prev.onError);
      }

      const onLaunch = () => {
        goTrick("打开看看");
      };
      const onError = () => {
        goTrick("去麦当劳APP，看奶昔大哥直播间");
        downloadAtOnce();
      };

      el.addEventListener("launch", onLaunch);
      el.addEventListener("error", onError);
      wxOpenLaunchListenerMap.set(el, { onLaunch, onError });
    });
  }, []);

  const scheduleBindAfterWxReady = useCallback(() => {
    bindWxOpenLaunchListeners();
    bindWxOpenDelayIdsRef.current.forEach((tid) => window.clearTimeout(tid));
    bindWxOpenDelayIdsRef.current = [50, 200, 500].map((ms) =>
      window.setTimeout(() => bindWxOpenLaunchListeners(), ms)
    );
  }, [bindWxOpenLaunchListeners]);

  const initWechatAuth = useCallback(async () => {
    const win: any = window;
    if (!getEnv().isWebInWeapp || !win.wx) return;

    try {
      const { data }: any = await apis.getWechatAuth();
      // setWxOaAppId(data.appId ?? "");
      win.wx.config({
        debug: false,
        appId: data.appId,
        timestamp: data.timestamp,
        nonceStr: data.nonce,
        signature: data.sign,
        jsApiList: [...WX_JS_API_LIST],
        openTagList: ["wx-open-launch-app"],
      });

      win.wx.ready(() => {
        scheduleBindAfterWxReady();
      });

      win.wx.error?.((res: unknown) => {
        Toast.show({ content: "微信配置失败，请稍后重试" });
        console.error("wx.config error:", res);
      });
    } catch (err) {
      console.error("微信配置失败:", err);
      Toast.show({ content: "微信配置失败，请稍后重试" });
    }
  }, [scheduleBindAfterWxReady]);

  useEffect(() => {
    document.title = PAGE_TITLE;
    initWechatAuth();
    return () => {
      bindWxOpenDelayIdsRef.current.forEach((tid) => window.clearTimeout(tid));
      bindWxOpenDelayIdsRef.current = [];
    };
  }, [initWechatAuth]);

  const visibleDanmaku = useMemo(() => {
    if (!poolLen) return [];
    return pool.slice(0, Math.min(DANMAKU_VISIBLE_COUNT, poolLen));
  }, [pool, poolLen]);

  const onFooterOpenInPlainBrowser = debounce(() => {
    (window as any).sensors?.track("buttonClick", {
      belong_page: window.location.href || "",
      button_name: "打开看看/去App",
      module_name: "直播间分享页",
    });
    tryOpenAppOrDownload(launchExtInfo);
  }, 1000, { leading: true, trailing: false });

  const onTopOpenBtnClick = debounce(() => {
    downloadAtOnce();
  }, 1000, { leading: true, trailing: false });

  useEffect(() => {
    (window as any).sensors?.track("customizePageView", {
      belong_page: "直播间H5分享页",
      page_status: 1,
      promotion_name: "奶昔大哥直播",
    });
  }, []);

  const { isWebInWeapp } = getEnv();

  const currentDateLabel = useMemo(() => formatLiveShareCurrentDate(), []);

  const dateLabelStyle = useMemo(
    () => ({
      left: `${LIVE_SHARE_DATE_LABEL_LAYOUT.left}px`,
      top: `${LIVE_SHARE_DATE_LABEL_LAYOUT.top}px`,
      fontSize: `${LIVE_SHARE_DATE_LABEL_LAYOUT.fontSize}px`,
    }),
    []
  );

  return (
    <div className="live-share-page">
      <header className="live-share-page__top">
        <p className="live-share-page__top-text">{SHARE_GUIDE_TEXT}</p>

        <div className="live-share-page__top-btn-wrap">
          {isWebInWeapp ? (
            <div className="live-share-page__btn-container">
              <img alt="" src={OPEN_BTN_IMG_URL} className="live-share-page__top-launch-face" />
              {/* @ts-ignore */}
                <wx-open-launch-app
                  style={WX_OPEN_LAUNCH_STYLE}
                  id="launch-share-top"
                  appid="wx6d8030a2f43b09a2"
                  extinfo={launchExtInfo}
                >
                  <script type="text/wxtag-template">
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        opacity: 0,
                      }}
                    />
                  </script>
                  {/* @ts-ignore */}
                </wx-open-launch-app>
            </div>
          ) : (
            <img
              alt=""
              src={OPEN_BTN_IMG_URL}
              className="live-share-page__top-btn-img"
              onClick={onTopOpenBtnClick}
            />
          )}
        </div>
      </header>

      <main className="live-share-page__body">
        <div className="live-share-page__body-visual">
          <img className="live-share-page__body-bg" src={SHARE_H5_BG_IMG} alt="" />
          <span className="live-share-page__body-date" style={dateLabelStyle}>
            {currentDateLabel}
          </span>
        </div>
      </main>

      <div className="live-share-page__bottom-slot">
        <footer className="live-share-page__footer">
          {isWebInWeapp ? (
            <div className="live-share-page__footer-btn live-share-page__footer-btn--wx-host live-share-page__btn-container">
              <img className="live-share-page__footer-btn-img" src={SHARE_H5_BTN_IMG} alt="" />
              {/* @ts-ignore */}
              <wx-open-launch-app
                style={WX_OPEN_LAUNCH_STYLE}
                id="launch-share-footer"
                appid="wx6d8030a2f43b09a2"
                extinfo={launchExtInfo}
              >
                <script type="text/wxtag-template">
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                    }}
                  />
                </script>
                {/* @ts-ignore */}
              </wx-open-launch-app>
            </div>
          ) : (
            <button
              type="button"
              className="live-share-page__footer-btn"
              onClick={onFooterOpenInPlainBrowser}
              aria-label={BOTTOM_CTA_TEXT}
            >
              <img className="live-share-page__footer-btn-img" src={SHARE_H5_BTN_IMG} alt="" />
            </button>
          )}
        </footer>

        <div className="live-share-page__danmaku" aria-live="polite">
          {visibleDanmaku.map((line: DanmakuItem, idx) => (
            <div key={idx} className="live-share-page__danmaku-item">
              <img className="live-share-page__danmaku-avatar" src={line.avatar} alt="" />
              <span className="live-share-page__danmaku-nickname">{line.nickname}</span>
              <span className="live-share-page__danmaku-content">{line.content}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LiveSharePage;
